"use client";

/**
 * Browser-side realtime client — one reconnecting WebSocket for the whole app.
 *
 * Responsibilities:
 *  - ref-counted channel subscriptions (multiple hooks can share a channel);
 *  - reconnect with exponential backoff (per the Vercel docs pattern);
 *  - resubscribe + emit a "reconnect" signal after any re-establish, so hooks
 *    can run a delta resync to catch anything missed while disconnected;
 *  - auth gate (`setEnabled`) and foreground gate (`pause`/`resume`).
 *
 * When `NEXT_PUBLIC_WS_URL` is unset the client stays "disabled" and never
 * connects — hooks then rely on their slow fallback polling.
 */
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { createClientLogger } from "@/lib/logging/client";
import type { RealtimeClientFrame, RealtimeEvent } from "./events";

const logger = createClientLogger("lib/realtime/client");

export type RealtimeStatus = "disabled" | "connecting" | "open" | "closed";

type EventListener = (event: RealtimeEvent) => void;
type StatusListener = (status: RealtimeStatus) => void;
type ReconnectListener = () => void;

const WS_ORIGIN = process.env.NEXT_PUBLIC_WS_URL?.replace(/\/+$/, "") || null;
const BASE_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

/**
 * How long a socket must stay open before the connection counts as healthy.
 *
 * The backoff used to reset on `open`, which sounds right and is not: the
 * gateway runs on ephemeral Vercel Fluid instances, so a socket is accepted
 * (101) and then dropped when the instance recycles — often within seconds.
 * Every one of those drops is preceded by a successful open, so resetting
 * there meant the backoff never escalated and the client reconnected roughly
 * once a second, forever. Production telemetry showed one user at 691
 * reconnects in 7 days, each one firing two analytics events and re-rendering
 * every subtree subscribed to `useRealtimeStatus`.
 *
 * Measuring the connection's *lifetime* at close instead is what the backoff
 * was always meant to express: a long-lived socket that finally blips earns a
 * fast retry; a socket that dies immediately does not.
 */
const STABLE_CONNECTION_MS = 30_000;

/**
 * Heartbeat cadence, and how long silence is allowed before we give up.
 *
 * What this is NOT: the fix for reconnect churn. Telemetry settles that —
 * 3,015 connects against 728 disconnects over two weeks, and
 * `realtime_disconnected` only fires on a genuine drop. Roughly three quarters
 * of reconnects followed the client closing its own socket (foreground/
 * background cycling, and auth state briefly reading as unknown), not the
 * server dropping it. Those causes are handled in `RealtimeProvider`.
 *
 * What this IS: protection against a half-open socket. When the peer goes away
 * without a close frame — a dropped mobile connection is the usual way — the
 * local `readyState` stays OPEN forever. No `close` event fires, so nothing
 * schedules a reconnect, and the client sits on a dead connection believing
 * realtime is healthy. Nothing else in this class detects that.
 *
 * The ping is what makes the silence meaningful: without traffic of our own we
 * could not distinguish "dead" from "quiet", since most channels are idle most
 * of the time. The gateway has always answered `{op:"ping"}` with
 * `{system:"pong"}` (gateway.ts) — the client simply never sent one.
 */
const CONNECTION_TIMEOUT_MS = 20_000;
const HEARTBEAT_INTERVAL_MS = 30_000;
const HEARTBEAT_SILENCE_LIMIT_MS = 75_000;

/**
 * Connect/disconnect telemetry is the only view we have of gateway health, but
 * under churn it is also a flood — it was a measurable share of the requests
 * competing with RSC navigations on a degraded connection. Report the first
 * few of every churn episode, then sample. `churn_count` carries the real
 * magnitude, so severity is still visible from a sampled event.
 */
function shouldReportChurnEvent(count: number): boolean {
  return count <= 5 || count % 25 === 0;
}

class RealtimeClient {
  private socket: WebSocket | null = null;
  private enabled = false;
  private paused = false;
  private online = true;
  private disposeSocketTimers: (() => void) | null = null;
  private probeSocket: (() => void) | null = null;
  private status: RealtimeStatus = WS_ORIGIN ? "closed" : "disabled";
  private backoffMs = BASE_BACKOFF_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private hasConnectedOnce = false;
  /** Consecutive short-lived connections; reset by a stable one. */
  private churnCount = 0;
  /** The caller's own `user:<id>` channel, learned from the server hello. */
  private userChannel: string | null = null;

  private readonly channelListeners = new Map<string, Set<EventListener>>();
  private readonly userListeners = new Set<EventListener>();
  private readonly statusListeners = new Set<StatusListener>();
  private readonly reconnectListeners = new Set<ReconnectListener>();

  get currentStatus(): RealtimeStatus {
    return this.status;
  }

  /**
   * A read-only snapshot for observers, and only for observers.
   *
   * The navigation watchdog needs to know what this socket was doing at the
   * instant a page got stuck. Production shows two connects for every close
   * and handshakes that take twelve seconds; whether that coincides with a
   * stuck navigation is the difference between "the realtime socket is a
   * separate annoyance" and "the realtime socket is starving the connection
   * the page is loading over". Correlating them needs both facts in one
   * event, and only this object holds one of them.
   *
   * Returns a copy of primitives: nothing here can be used to steer the
   * connection, which is what keeps this from becoming a second control path.
   */
  get diagnostics(): {
    status: RealtimeStatus;
    churnCount: number;
    backoffMs: number;
    paused: boolean;
    readyState: number | null;
  } {
    return {
      status: this.status,
      churnCount: this.churnCount,
      backoffMs: this.backoffMs,
      paused: this.paused,
      readyState: this.socket ? this.socket.readyState : null,
    };
  }

  /** Auth gate: connect only while signed in (server 401s otherwise). */
  setEnabled(enabled: boolean): void {
    if (enabled === this.enabled) return;
    this.enabled = enabled;
    if (enabled) {
      // Signing in is a fresh intent, not a retry of a failing connection.
      this.resetBackoff();
      this.openIfNeeded();
    } else {
      this.hasConnectedOnce = false;
      this.userChannel = null;
      this.resetBackoff();
      this.close("disabled");
    }
  }

  /** Clear accumulated backoff for a deliberate (re)connect. */
  private resetBackoff(): void {
    this.backoffMs = BASE_BACKOFF_MS;
    this.churnCount = 0;
  }

  /** Foreground gate: socket dies on background anyway, so close it cleanly. */
  pause(): void {
    if (this.paused) return;
    this.paused = true;
    this.close(WS_ORIGIN ? "closed" : "disabled");
  }

  resume(): void {
    if (!this.paused) {
      this.probeSocket?.();
      this.openIfNeeded();
      return;
    }
    this.paused = false;
    // Returning to the foreground is a new attempt on (usually) a new network,
    // so it should not inherit backoff accumulated while backgrounded.
    this.resetBackoff();
    this.openIfNeeded();
  }

  /** A restored network may have a new route even when the old socket says OPEN. */
  setOnline(online: boolean): void {
    if (this.online === online) return;
    this.online = online;
    this.resetBackoff();
    this.close(WS_ORIGIN ? "closed" : "disabled");
    if (online) this.openIfNeeded();
  }

  subscribe(channel: string, listener: EventListener): () => void {
    let set = this.channelListeners.get(channel);
    const isFirst = !set || set.size === 0;
    if (!set) {
      set = new Set();
      this.channelListeners.set(channel, set);
    }
    set.add(listener);
    if (isFirst) this.send({ op: "sub", channel });
    return () => {
      const current = this.channelListeners.get(channel);
      if (!current) return;
      current.delete(listener);
      if (current.size === 0) {
        this.channelListeners.delete(channel);
        this.send({ op: "unsub", channel });
      }
    };
  }

  /**
   * Subscribe to the caller's own `user:<id>` channel. The server auto-joins it
   * on connect, so no control frame is sent — we just route frames whose
   * channel matches the one announced in the server hello.
   */
  subscribeUser(listener: EventListener): () => void {
    this.userListeners.add(listener);
    return () => {
      this.userListeners.delete(listener);
    };
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  onReconnect(listener: ReconnectListener): () => void {
    this.reconnectListeners.add(listener);
    return () => {
      this.reconnectListeners.delete(listener);
    };
  }

  // ---- internals -------------------------------------------------------------

  private setStatus(status: RealtimeStatus): void {
    if (status === this.status) return;
    this.status = status;
    for (const listener of [...this.statusListeners]) {
      try {
        listener(status);
      } catch {
        // a status listener threw — ignore
      }
    }
  }

  private openIfNeeded(): void {
    if (!WS_ORIGIN || !this.enabled || this.paused || !this.online) return;
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    this.connect();
  }

  private connect(): void {
    if (typeof window === "undefined" || !WS_ORIGIN) return;
    this.clearReconnectTimer();
    this.disposeSocketTimers?.();
    this.setStatus("connecting");

    let socket: WebSocket;
    try {
      socket = new WebSocket(`${WS_ORIGIN}/api/ws`);
    } catch (error) {
      logger.warn("realtime.connect_threw", {
        message: error instanceof Error ? error.message : String(error),
      });
      captureClientEvent(ANALYTICS_EVENTS.REALTIME_FALLBACK_ENGAGED, {
        reason: "constructor_threw",
        error_message: error instanceof Error ? error.message : undefined,
      });
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    // Per-socket, NOT an instance field. Close events can arrive out of order
    // — a superseded socket may fire `close` after its replacement has already
    // opened — and an instance field would then measure the new connection's
    // lifetime against the old one's close, mis-scoring a healthy socket as
    // churn (or the reverse). The closure keeps each socket's own clock.
    let socketOpenedAt = 0;
    let lastFrameAt = 0;
    let reapedForSilence = false;
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    let connectionTimer: ReturnType<typeof setInterval> | null = null;

    const stopHeartbeat = () => {
      if (connectionTimer !== null) {
        clearInterval(connectionTimer);
        connectionTimer = null;
      }
      if (heartbeatTimer !== null) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
    };

    const startHeartbeat = () => {
      stopHeartbeat();
      lastFrameAt = Date.now();
      heartbeatTimer = setInterval(probe, HEARTBEAT_INTERVAL_MS);
    };
    const loseConnection = () => {
      // Do not wait for a close handshake on a broken transport. Its eventual
      // events are ignored after this socket loses ownership.
      handleClose();
      try {
        socket.close();
      } catch {
        /* already unreachable */
      }
    };
    const probe = () => {
      if (this.socket !== socket || socket.readyState !== WebSocket.OPEN)
        return;
      if (Date.now() - lastFrameAt > HEARTBEAT_SILENCE_LIMIT_MS) {
        reapedForSilence = true;
        loseConnection();
        return;
      }
      try {
        socket.send(JSON.stringify({ op: "ping" }));
      } catch {
        reapedForSilence = true;
        loseConnection();
      }
    };
    this.disposeSocketTimers = stopHeartbeat;
    this.probeSocket = probe;
    connectionTimer = setInterval(() => {
      if (this.socket === socket && socket.readyState === WebSocket.CONNECTING)
        loseConnection();
    }, CONNECTION_TIMEOUT_MS);

    socket.addEventListener("open", () => {
      if (
        this.socket !== socket ||
        !this.enabled ||
        this.paused ||
        !this.online
      )
        return;
      socketOpenedAt = Date.now();
      // Realtime health is invisible from the server side alone: a gateway
      // that accepts the upgrade and then drops every socket looks healthy in
      // route logs. The client is the only place that sees reconnect churn.
      //
      // NOTE: the backoff is deliberately NOT reset here — see
      // STABLE_CONNECTION_MS. An `open` only proves the gateway accepted the
      // upgrade, not that the connection is usable.
      if (shouldReportChurnEvent(this.churnCount + 1)) {
        captureClientEvent(ANALYTICS_EVENTS.REALTIME_CONNECTED, {
          is_reconnect: this.hasConnectedOnce,
          backoff_ms: this.backoffMs,
          churn_count: this.churnCount,
        });
      }
      this.setStatus("open");
      for (const channel of this.channelListeners.keys()) {
        this.send({ op: "sub", channel });
      }
      // Even the first connection has a gap after the initial HTTP query.
      this.emitReconnect();
      this.hasConnectedOnce = true;
      startHeartbeat();
    });

    socket.addEventListener("message", (event) => {
      if (this.socket !== socket) return;
      // Any frame proves the peer is alive — `pong`, an event, anything.
      lastFrameAt = Date.now();
      this.handleFrame(typeof event.data === "string" ? event.data : "");
    });

    const handleClose = () => {
      stopHeartbeat();
      const isCurrent = this.socket === socket;
      if (isCurrent) {
        this.socket = null;
        this.disposeSocketTimers = null;
        this.probeSocket = null;
      }
      // A socket we already replaced or deliberately discarded must not touch
      // the live connection's backoff, churn count or reconnect schedule.
      if (!isCurrent) return;

      if (this.enabled && !this.paused && this.online) {
        // The connection's lifetime — not the fact that it opened — decides
        // whether the backoff resets. A socket that survived the stability
        // window earns a fast retry; one that died immediately escalates.
        const lifetimeMs =
          socketOpenedAt === 0 ? 0 : Date.now() - socketOpenedAt;
        // Score on the lifetime the peer actually PROVED, not the wall clock.
        //
        // A socket the heartbeat reaped spent its last
        // HEARTBEAT_SILENCE_LIMIT_MS in silence, and that silence is exactly
        // what we refuse to count as health. Using wall-clock time here would
        // let the two new mechanisms contradict each other: a gateway that
        // never answers a ping is reaped at ~90s, which clears
        // STABLE_CONNECTION_MS, so every cycle would reset the backoff and
        // report an unsampled disconnect — a permanent 90s reconnect loop with
        // the telemetry flood `shouldReportChurnEvent` exists to prevent.
        //
        // Measuring to the last frame instead separates the two cases on their
        // own evidence. A socket that ran healthily for minutes and then lost
        // its peer still scores stable and earns its fast retry; one whose
        // only ever frame was the server hello scores ~0 and escalates.
        const provenLifetimeMs =
          socketOpenedAt === 0 || !reapedForSilence
            ? lifetimeMs
            : Math.max(0, lastFrameAt - socketOpenedAt);
        const wasStable = provenLifetimeMs >= STABLE_CONNECTION_MS;
        if (wasStable) {
          this.backoffMs = BASE_BACKOFF_MS;
          this.churnCount = 0;
        } else {
          this.churnCount += 1;
        }

        if (wasStable || shouldReportChurnEvent(this.churnCount)) {
          captureClientEvent(ANALYTICS_EVENTS.REALTIME_DISCONNECTED, {
            had_connected: this.hasConnectedOnce,
            backoff_ms: this.backoffMs,
            lifetime_ms: lifetimeMs,
            proven_lifetime_ms: provenLifetimeMs,
            reaped_for_silence: reapedForSilence,
            was_stable: wasStable,
            churn_count: this.churnCount,
          });
        }
        this.setStatus("closed");
        this.scheduleReconnect();
      }
    };
    socket.addEventListener("close", handleClose);

    socket.addEventListener("error", () => {
      // a "close" event always follows; reconnect is scheduled there.
    });
  }

  private handleFrame(data: string): void {
    if (!data) return;
    let frame: unknown;
    try {
      frame = JSON.parse(data);
    } catch {
      return;
    }
    if (!frame || typeof frame !== "object") return;
    const obj = frame as Record<string, unknown>;

    // System frames: learn our personal channel from the hello, ignore others.
    if (typeof obj.system === "string") {
      if (obj.system === "hello" && typeof obj.channel === "string") {
        this.userChannel = obj.channel;
      }
      return;
    }

    const channel = obj.channel;
    const event = obj.event;
    if (typeof channel !== "string" || !event || typeof event !== "object") {
      return;
    }
    const realtimeEvent = event as RealtimeEvent;
    this.dispatch(this.channelListeners.get(channel), channel, realtimeEvent);
    if (channel === this.userChannel) {
      this.dispatch(this.userListeners, channel, realtimeEvent);
    }
  }

  private dispatch(
    listeners: Set<EventListener> | undefined,
    channel: string,
    event: RealtimeEvent,
  ): void {
    if (!listeners || listeners.size === 0) return;
    for (const listener of [...listeners]) {
      try {
        listener(event);
      } catch (error) {
        logger.warn("realtime.listener_threw", {
          channel,
          message: error instanceof Error ? error.message : String(error),
        });
      }
    }
  }

  private send(frame: RealtimeClientFrame): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(JSON.stringify(frame));
    } catch {
      // not open — resubscribed on next open
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || !this.enabled || this.paused || !this.online)
      return;
    // Equal jitter. Without it every client that shares a recycled gateway
    // instance wakes up in lockstep and reconnects as one burst, which is the
    // fastest way to make the replacement instance recycle too.
    const delay = this.backoffMs / 2 + Math.random() * (this.backoffMs / 2);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openIfNeeded();
    }, delay);
    this.backoffMs = Math.min(this.backoffMs * 2, MAX_BACKOFF_MS);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private close(status: RealtimeStatus): void {
    this.clearReconnectTimer();
    const socket = this.socket;
    this.disposeSocketTimers?.();
    this.disposeSocketTimers = null;
    this.probeSocket = null;
    this.socket = null;
    if (socket) {
      try {
        socket.close();
      } catch {
        // ignore
      }
    }
    this.setStatus(status);
  }

  private emitReconnect(): void {
    for (const listener of [...this.reconnectListeners]) {
      try {
        listener();
      } catch {
        // ignore
      }
    }
  }
}

export const realtimeClient = new RealtimeClient();
